import React, {
  useCallback, useEffect, useMemo, useReducer, useRef,
} from 'react'
import { useParams } from 'react-router-dom'
import { GroupNavi, HeaderNavi } from '../../components/navigation'
import BaseModal from '../../components/common/BaseModal'
import Loading from '../../components/common/Loading'
import MamoriosMapModal from '../../components/overview/MamoriosMapModal'
import RentalModal from '../../components/overview/RentalModal'
import SearchBar from '../../components/overview/SearchBar'
import GroupMamorioList from '../../components/overview/GroupMamorioList'
import DevicesList from '../../components/overview/DevicesList'
import GpsTrackersList from '../../components/overview/GpsTrackersList'
import QrTagsList from '../../components/overview/QrTagsList'
import RfidTagsList from '../../components/overview/RfidTagsList'
import Pagination from '../../components/common/Pagination'
import { getMamoriosWithLocation } from '../../helpers/CommonMamorioHelper'
import Unauthorized from '../Unauthorized'
import {
  getGroupDevices,
  getGroupMamorios,
  getGroupMamoriosWithoutUpdatingState,
  SortOrderAsc,
  SortOrderDesc,
} from '../../modules/Mamorio'
import { fetchGroupEventTypes } from '../../modules/QrTagEventTypes'
import { getRootGroupId } from '../../modules/common/session'
import ChannelService from '../../modules/ChannelService'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { closeModal, showModal } from '../../modules/UI'
import { toId } from '../../utils/commonUtils'
import type { Filter } from '../../modules/Mamorio'
import { miniUsersIcon } from '../../icons'
import './Overview.css'
import { ERROR_MODAL } from '../../components/common/RootModal'
import { selectLoadingState } from '../../store'
import { getErrorString } from '../../utils/errorUtils'
import ReloadButton from '../../components/overview/ReloadButton'
import ViewAllMamoriosInMapButton from '../../components/overview/ViewAllMamoriosInMapButton'
import LendMamorioButton from '../../components/overview/LendMamorioButton'
import GroupMamorioCount from '../../components/overview/GroupMamorioCount'
import teamHasDevices from '../../helpers/deviceFeature'

enum OverviewActionType {
  Paginate = 'paginate',
  ChangeGroupId = 'changeGroupId',
  UpdateFilter = 'updateFilter',
  ListType = 'listType',
}

const MamorioListType = 'mamorio'
const DeviceListType = 'device'
const GpsTrackerListType = 'gps_tracker'
const QrTagListType = 'qr_tag'
const RfidListType = 'rfid'
type ListType = typeof MamorioListType
  | typeof DeviceListType
  | typeof GpsTrackerListType
  | typeof QrTagListType
  | typeof RfidListType
const LIST_TYPE_STORAGE_PREFIX = 'overview:listType:'

type OverviewState = {
  page?: number
  groupId?: GroupId
  filter?: Filter
  listType: ListType
  qrTagKeyword: string
  qrTagLatestEventType: string
  rfidTagKeyword: string
}

type GruopSortDeps = {
  groupId: GroupId | null
  filter: Filter | null
  listType: ListType | null
}

type OverviewAction =
  | {
    type: OverviewActionType.Paginate,
    payload: number
  }
  | {
    type: OverviewActionType.ChangeGroupId,
    payload: {
      groupId: GroupId
      listType: ListType
    }
  }
  | {
    type: OverviewActionType.UpdateFilter
    payload: any
  }
  | {
    type: OverviewActionType.ListType,
    payload: ListType
  }
  | {
    type: 'qrTagKeyword',
    payload: string
  }
  | {
    type: 'qrTagLatestEventType',
    payload: string
  }
  | {
    type: 'rfidTagKeyword',
    payload: string
  }

const reducer = (state: OverviewState, action: OverviewAction): OverviewState => {
  switch (action.type) {
    case OverviewActionType.Paginate:
      return {
        ...state,
        page: action.payload,
      }
    case OverviewActionType.ChangeGroupId:
      return {
        ...state,
        page: 1,
        groupId: action.payload.groupId,
        listType: action.payload.listType,
      }
    case OverviewActionType.UpdateFilter:
      return {
        ...state,
        filter: action.payload,
      }
    case OverviewActionType.ListType:
      return {
        ...state,
        listType: action.payload,
      }
    case 'qrTagKeyword':
      return {
        ...state,
        qrTagKeyword: action.payload,
      }
    case 'qrTagLatestEventType':
      return {
        ...state,
        qrTagLatestEventType: action.payload,
      }
    case 'rfidTagKeyword':
      return {
        ...state,
        rfidTagKeyword: action.payload,
      }
    default:
      throw new Error()
  }
}

const getListTypeStorageKey = (groupId: GroupId) => `${LIST_TYPE_STORAGE_PREFIX}${groupId}`

const loadStoredListType = (groupId: GroupId): ListType | null => {
  const stored = localStorage.getItem(getListTypeStorageKey(groupId))
  if (
    stored === MamorioListType
    || stored === DeviceListType
    || stored === GpsTrackerListType
    || stored === QrTagListType
    || stored === RfidListType
  ) {
    return stored
  }
  return null
}

const resolveStoredListType = (
  groupId: GroupId,
  availableListTypes: ListType[],
): ListType => {
  const storedListType = loadStoredListType(groupId)
  return storedListType && availableListTypes.includes(storedListType)
    ? storedListType
    : MamorioListType
}

const Overview = () => {
  const { group_id }: { group_id: string } = useParams()

  const appDispatch = useAppDispatch()
  const team = useAppSelector((state) => state.team)
  const mamorios = useAppSelector((state) => state.mamorio.mamorios)
  const loading = useAppSelector(selectLoadingState)
  const user = useAppSelector((state) => state.user)
  const hasDeviceFeature = teamHasDevices(team)
  const hasGpsTrackerFeature = Boolean(team.gps_tracker_enabled)
  const hasQrTagFeature = Boolean(team.qr_tag_enabled)
  const hasRfidTagFeature = team.rfid_tag_enabled === true
  const hasMamorioCsvDownloadFeature = Boolean(team.mamorio_csv_download_enabled)
  const rentalMode = Boolean(team.groups?.some((group) => group.mode === 'rental'))
  const canUseAdvancedSelector = hasGpsTrackerFeature || hasQrTagFeature || hasRfidTagFeature
  const availableListTypes = useMemo<ListType[]>(() => {
    const listTypes: ListType[] = [MamorioListType]

    if (hasDeviceFeature) listTypes.push(DeviceListType)
    if (hasGpsTrackerFeature) listTypes.push(GpsTrackerListType)
    if (hasQrTagFeature) listTypes.push(QrTagListType)
    if (hasRfidTagFeature) listTypes.push(RfidListType)

    return listTypes
  }, [
    hasDeviceFeature,
    hasGpsTrackerFeature,
    hasQrTagFeature,
    hasRfidTagFeature,
  ])
  const initialGroupId = group_id ? toId<GroupId>(group_id) : getRootGroupId()

  const [state, dispatch] = useReducer(reducer, {
    filter: {
      order: SortOrderAsc,
      sort: SortOrderDesc,
    },
    page: 1,
    groupId: initialGroupId,
    listType: resolveStoredListType(initialGroupId, availableListTypes),
    qrTagKeyword: '',
    qrTagLatestEventType: '',
    rfidTagKeyword: '',
  })
  const isCurrentListTypeAvailable = availableListTypes.includes(state.listType)
  const activeListType = isCurrentListTypeAvailable ? state.listType : MamorioListType

  const qrTagEventTypesEntry = useAppSelector((store) => (
    state.groupId ? store.qrTagEventTypes.entries[state.groupId] : undefined
  ))
  const qrTagEventTypeOptions = useMemo(() => {
    const values = (qrTagEventTypesEntry?.values ?? []).map((entry) => entry.name).filter((value) => value.length > 0)
    return Array.from(new Set(values))
  }, [qrTagEventTypesEntry])
  const currentGroup = useMemo(() => (
    team.groups?.find((child) => toId(child.id) === state.groupId)
  ), [state.groupId, team.groups])

  const loadList = useCallback(async (useDeviceApi: boolean) => {
    dispatch({
      type: OverviewActionType.Paginate,
      payload: 1,
    })

    if (useDeviceApi && hasDeviceFeature) {
      dispatch({
        type: OverviewActionType.ListType,
        payload: DeviceListType,
      })
    } else {
      dispatch({
        type: OverviewActionType.ListType,
        payload: MamorioListType,
      })
    }
  }, [dispatch, hasDeviceFeature])

  const handleListTypeChange = (listType: ListType) => {
    dispatch({
      type: OverviewActionType.ListType,
      payload: listType,
    })
    dispatch({
      type: OverviewActionType.Paginate,
      payload: 1,
    })
  }

  const reloadList = async () => {
    if (!state.groupId || !state.filter) return
    if (!isCurrentListTypeAvailable) return
    if (
      activeListType === GpsTrackerListType
      || activeListType === QrTagListType
      || activeListType === RfidListType
    ) {
      return
    }
    try {
      if (activeListType === DeviceListType) {
        await appDispatch(getGroupDevices({
          groupId: state.groupId,
          filter: state.filter,
        })).unwrap()
      } else if (activeListType === MamorioListType) {
        await appDispatch(getGroupMamorios({
          groupId: state.groupId,
          filter: state.filter,
        })).unwrap()
      }
    } catch (e: unknown) {
      appDispatch(closeModal({ modalType: ERROR_MODAL }))
      appDispatch(showModal({
        modalType: ERROR_MODAL,
        modalProps: {
          message: getErrorString(e),
        },
      }))
    }
  }

  const showMamoriosMapModal = () => {
    if (!mamorios) return

    const showMap = (_mamorios: IMamorio[]) => {
      appDispatch(showModal({
        modalType: MamoriosMapModal,
        modalProps: {
          mamorios: getMamoriosWithLocation(_mamorios),
        },
      }))
    }

    if (state.page === 1 && mamorios.length < 29) {
      showMap(mamorios)
      return
    }

    appDispatch(getGroupMamoriosWithoutUpdatingState({
      groupId: state.groupId!,
      filter: {
        page: 1,
        maxMamorios: true,
      },
    })).unwrap().then((result) => {
      showMap(result.mamorios)
    }).catch((error) => {
      console.error(getErrorString(error))
      showMap(mamorios)
    })
  }

  const showRentalModal = (group: IGroup) => {
    appDispatch(showModal({
      modalType: BaseModal,
      modalProps: {
        title: 'このグループのMAMORIOを貸し出す',
        onActionConfirmed: undefined,
        children: <RentalModal group={group} />,
      },
    }))
  }

  const updateGroupFilter = (newFilter: Filter) => {
    dispatch({
      type: OverviewActionType.UpdateFilter,
      payload: { ...state.filter, ...newFilter },
    })
  }

  const onClickHeader = async (filter: Filter, successCallback: () => void) => {
    const newFilter = { ...state.filter, ...filter }

    dispatch({
      type: OverviewActionType.UpdateFilter,
      payload: newFilter,
    })

    successCallback()
  }

  const onChangePage = (pageNumber: number) => {
    dispatch({
      type: OverviewActionType.Paginate,
      payload: pageNumber,
    })
  }

  useEffect(() => {
    const nextGroupId = group_id ? toId<GroupId>(group_id) : getRootGroupId()
    dispatch({
      type: OverviewActionType.ChangeGroupId,
      payload: {
        groupId: nextGroupId,
        listType: resolveStoredListType(nextGroupId, availableListTypes),
      },
    })
  }, [availableListTypes, dispatch, group_id])

  useEffect(() => {
    if (typeof window.ChannelIO === 'undefined') {
      ChannelService.bootWithMember(user.user?.id)
    }
  }, [user.user?.id])

  const prevDepsRef = useRef<GruopSortDeps>()

  useEffect(() => {
    if (!state.groupId) return
    if (!availableListTypes.includes(state.listType)) return
    localStorage.setItem(getListTypeStorageKey(state.groupId), state.listType)
  }, [availableListTypes, state.groupId, state.listType])

  useEffect(() => {
    if (!state.groupId) return
    if (!isCurrentListTypeAvailable || activeListType !== QrTagListType) return
    appDispatch(fetchGroupEventTypes({ groupId: state.groupId }))
  }, [activeListType, appDispatch, isCurrentListTypeAvailable, state.groupId])

  useEffect(() => {
    if (!state.groupId || !state.filter) return
    if (!isCurrentListTypeAvailable) return

    const newDeps = {
      groupId: state.groupId,
      filter: state.filter,
      listType: activeListType,
    }

    const dependenciesChanged = JSON.stringify(prevDepsRef.current) !== JSON.stringify(newDeps)
    const currentPage = dependenciesChanged ? 1 : state.page
    if (
      activeListType === GpsTrackerListType
      || activeListType === QrTagListType
      || activeListType === RfidListType
    ) {
      // GPSトラッカー・QRタグ・RFIDの場合は何もしない（各コンポーネント内で独自にfetchする）
      return
    }

    if (activeListType === DeviceListType) {
      appDispatch(getGroupDevices({
        groupId: state.groupId,
        filter: { ...state.filter, page: state.page },
      }))
    } else if (activeListType === MamorioListType) {
      appDispatch(getGroupMamorios({
        groupId: state.groupId,
        filter: { ...state.filter, page: currentPage },
      }))
      // TODO: ソート時に2回fetchしてしまう
      if (dependenciesChanged) {
        onChangePage(1)
      }
    }
    prevDepsRef.current = newDeps
  }, [activeListType, appDispatch, isCurrentListTypeAvailable, state.groupId, state.filter, state.page])

  if (!currentGroup) {
    return (
      <div className="content">
        <HeaderNavi />
        <Unauthorized />
      </div>
    )
  }

  return (
    <div
      className="content overview-page"
      data-testid="overview"
    >
      <HeaderNavi />
      <Loading show={loading} />
      <GroupNavi>
        <div className="overview-page__main">
          <div className="overview-title">
            <div className="overview-title__heading">
              <div className="group-name" data-testid="current-group-name">
                <img src={miniUsersIcon} alt="group-icon" />
                {currentGroup.name}
              </div>
              <GroupMamorioCount mamoriosCount={currentGroup.mamorios_count} />
            </div>
            <div>
              <ReloadButton onClick={reloadList} />
              { isCurrentListTypeAvailable
                && mamorios
                && getMamoriosWithLocation(mamorios!)?.length > 0
                && <ViewAllMamoriosInMapButton onClick={showMamoriosMapModal} />}
              {currentGroup.mode === 'rental'
                && <LendMamorioButton onClick={() => showRentalModal(currentGroup)} />}
            </div>
          </div>
          <hr />
          <SearchBar
            rentalMode={rentalMode}
            loadList={loadList}
            updateGroupFilter={updateGroupFilter}
            showDeviceSwitch={hasDeviceFeature}
            showSelector={canUseAdvancedSelector}
            currentListType={activeListType}
            listTypes={availableListTypes}
            onListTypeChange={handleListTypeChange}
            memoEnabled={currentGroup.memo_enabled}
            qrTagKeyword={state.qrTagKeyword}
            onQrTagSearch={(keyword) => {
              dispatch({ type: 'qrTagKeyword', payload: keyword })
            }}
            qrTagLatestEventType={state.qrTagLatestEventType}
            onQrTagLatestEventTypeChange={(eventType) => {
              dispatch({ type: 'qrTagLatestEventType', payload: eventType })
            }}
            qrTagEventTypeOptions={qrTagEventTypeOptions}
            rfidTagKeyword={state.rfidTagKeyword}
            onRfidTagSearch={(keyword) => {
              dispatch({ type: 'rfidTagKeyword', payload: keyword })
            }}
          />
          <div
            className="overview-page__list-scroll"
            data-testid="overview-list-scroll-region"
            role="region"
            aria-label="物品一覧"
          >
            {(() => {
              if (!isCurrentListTypeAvailable) return null
              if (activeListType === MamorioListType) {
                return (
                  <GroupMamorioList
                    group={currentGroup}
                    filter={state.filter}
                    onClickHeader={onClickHeader}
                    onUpdateMamorio={reloadList}
                    csvDownloadEnabled={hasMamorioCsvDownloadFeature}
                    rentalMode={rentalMode}
                  />
                )
              }
              if (activeListType === DeviceListType) {
                return (
                  <DevicesList
                    filter={state.filter}
                    group={currentGroup}
                  />
                )
              }
              if (activeListType === QrTagListType) {
                return (
                  <QrTagsList
                    group={currentGroup}
                    keyword={state.qrTagKeyword}
                    latestEventType={state.qrTagLatestEventType}
                  />
                )
              }
              if (activeListType === RfidListType) {
                return (
                  <RfidTagsList
                    group={currentGroup}
                    keyword={state.rfidTagKeyword}
                  />
                )
              }
              return (
                <GpsTrackersList
                  group={currentGroup}
                />
              )
            })()}
          </div>
          {activeListType === MamorioListType && isCurrentListTypeAvailable && mamorios && mamorios.length > 0
            && <Pagination onChangePage={onChangePage} />}
        </div>
      </GroupNavi>
    </div>
  )
}

export default Overview
